import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { money, fmtDate } from "@/mechanic/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, Truck, Phone, Mail, User } from "lucide-react";

export default function SupplierDetail() {
  const { supplierId } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["mech-supplier-stats", supplierId],
    queryFn: async () => (await api.get(`/suppliers/${supplierId}/stats`)).data,
    enabled: !!supplierId,
  });

  if (isLoading) return <div className="p-8 text-muted-foreground">Loading…</div>;
  if (!data) return <div className="p-8 text-muted-foreground">Supplier not found.</div>;

  const s = data.supplier;

  return (
    <div className="p-8" data-testid="supplier-detail-page">
      <Button variant="ghost" onClick={() => navigate("/mechanic/suppliers")} className="mb-4" data-testid="back-to-suppliers">
        <ArrowLeft className="h-4 w-4 mr-1" />Back to suppliers
      </Button>

      <Card className="p-6 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Truck className="h-5 w-5 text-muted-foreground" />
              <h1 className="font-heading text-3xl" data-testid="supplier-name">{s.name}</h1>
            </div>
            <div className="flex flex-col gap-1 text-sm text-muted-foreground">
              {s.contact_name && <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" />{s.contact_name}</span>}
              {s.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{s.phone}</span>}
              {s.email && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{s.email}</span>}
              {s.supplies && <span>{s.supplies}</span>}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-6 text-center">
            <div><div className="text-2xl font-bold text-emerald-400 font-mono-data" data-testid="supplier-spend">{money(data.total_spend)}</div><div className="text-xs text-muted-foreground uppercase">Total spent</div></div>
            <div><div className="text-2xl font-bold" data-testid="supplier-parts">{data.unique_parts}</div><div className="text-xs text-muted-foreground uppercase">Unique parts</div></div>
            <div><div className="text-2xl font-bold">{data.last_purchase ? fmtDate(data.last_purchase) : "—"}</div><div className="text-xs text-muted-foreground uppercase">Last purchase</div></div>
          </div>
        </div>
      </Card>

      <h2 className="font-heading text-xl mb-3">Parts bought from this supplier</h2>
      {data.parts.length === 0 ? (
        <Card className="p-6 text-center text-muted-foreground">
          No parts recorded for this supplier yet. On any Work Order, pick this supplier on a part line to start tracking.
        </Card>
      ) : (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Part</TableHead>
                <TableHead>Part #</TableHead>
                <TableHead className="text-right">Qty bought</TableHead>
                <TableHead className="text-right">Last cost</TableHead>
                <TableHead className="text-right">Total spent</TableHead>
                <TableHead>Last purchase</TableHead>
                <TableHead className="text-right">Jobs</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.parts.map((p, i) => (
                <TableRow key={i} data-testid={`part-row-${i}`}>
                  <TableCell className="font-medium">{p.name || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{p.part_number || "—"}</TableCell>
                  <TableCell className="text-right font-mono-data">{p.qty}</TableCell>
                  <TableCell className="text-right font-mono-data">{money(p.last_cost)}</TableCell>
                  <TableCell className="text-right font-mono-data font-semibold">{money(p.spend)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{p.last_date ? fmtDate(p.last_date) : "—"}</TableCell>
                  <TableCell className="text-right">{p.wo_count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
